/**
 * Secured-asset netting — shared by the Estimated Outcome Waterfall and the
 * Asset list panel.
 *
 * Purpose:
 * - Net a secured creditor's balance against the asset(s) that secure it so the
 *   "true" (residual) value of an encumbered asset is surfaced, secured recovery
 *   is capped at the value of its collateral, and any secured shortfall is pushed
 *   down to rank pari passu with unsecured creditors.
 *
 * Data model (no schema change — everything is derived):
 * - Assets ↔ security interests ↔ creditors are joined through the polymorphic
 *   `recordLinks` table.
 * - An SI's owed balance is its OWN `owedAmount` figure when it has one (a PoD /
 *   document balance, a practitioner override, or the linked-creditor rollup) —
 *   "what you see on the SI panel is what's netted". When the SI has no own
 *   figure, we fall back to the sum of the `amount` on the creditor rows linked
 *   to it (`liabilityEntries.amount`).
 *
 * Grouping (confirmed product decision): net **per security interest**. An SI that
 * covers many assets (e.g. an all-assets / ALLPAP charge) nets against the combined
 * value of all assets linked to it. An asset linked to N SIs contributes
 * `assetValue / N` to each SI's collateral so it is never double-counted.
 *
 * Fallback: a secured creditor / SI with **no linked asset** keeps today's
 * behaviour — a full secured-priority deduction at its owed amount — because there
 * is no collateral to net against. Netting only applies once an asset is linked.
 */

import type { AssetEntryUI } from "./types";

// ---------------------------------------------------------------------------
// Minimal input shapes (narrowed to what the netting reads)
// ---------------------------------------------------------------------------

export interface NettingLiability {
  _id: string;
  status: "active" | "merged";
  amount?: number;
  /** Mirror flag kept in sync server-side ("has at least one active SI link"). */
  secured?: boolean;
  /** Excluded creditors contribute nothing to any secured debt. */
  isExcluded?: boolean;
}

export interface NettingLink {
  detailType: "security_interest" | "loan" | "ato_sro";
  detailId: string;
  anchorType: "asset" | "creditor";
  anchorId: string;
}

export interface NettingSecurityInterest {
  _id: string;
  status: "active" | "merged";
  /**
   * The security interest's own owed figure — a Proof-of-Debt / document
   * balance, a practitioner override, or the linked-creditor rollup. When set,
   * this is the canonical "what you see is what's netted" debt for the SI.
   */
  owedAmount?: number;
  /**
   * Excluded security interests are dropped entirely — no secured deduction and
   * no reduction of the collateral they were registered against (e.g. a party
   * that has discharged its PPSR registration).
   */
  isExcluded?: boolean;
}

/** Per-asset value selector for the active scenario (null contributes $0). */
export type AssetValueSelector = (a: AssetEntryUI) => number | null;

// ---------------------------------------------------------------------------
// Output shapes
// ---------------------------------------------------------------------------

export interface SecuredInterestGroup {
  siId: string;
  /** Active asset ids linked to this security interest. */
  assetIds: string[];
  /** Σ apportioned asset values securing this SI (this scenario). */
  collateralValue: number;
  /** Σ amount of the creditor rows linked to this SI. */
  owed: number;
  /** min(owed, collateralValue) — what the secured creditor takes from collateral. */
  recovered: number;
  /** collateralValue − recovered — surplus that flows to the free pool. */
  residualToPool: number;
  /** owed − recovered — shortfall that ranks pari passu with unsecured. */
  shortfall: number;
}

/**
 * Per-asset breakdown of the secured deduction — ids + amounts only (labels are
 * resolved in the UI). Lets the Asset panel show a creditor-traceable calc:
 * asset value − (Σ security interests, each itemised by its linked creditors).
 */
export interface AssetSecuredBreakdown {
  /** Σ apportioned secured balance against this asset (uncapped). Equals the
   *  asset's entry in `securedByAsset`. */
  totalDeduction: number;
  sis: Array<{
    siId: string;
    /** Apportioned owed this security interest takes from THIS asset. */
    siDeduction: number;
    /** owed came from the SI's own `owedAmount`, not the linked-creditor sum. */
    declaredOverride: boolean;
    /** Linked creditors, apportioned to this asset (sum to `siDeduction`). */
    creditors: Array<{ creditorId: string; amount: number }>;
  }>;
}

export interface SecuredNettingResult {
  /** SI groups that have at least one linked asset (the netted ones). */
  groups: SecuredInterestGroup[];
  /** Σ gross value of all active, non-excluded assets. */
  totalGrossAssets: number;
  /** Σ unencumbered asset values + Σ residualToPool — the figure to open the waterfall. */
  totalNetAssets: number;
  /** Σ recovered across netted SI groups (consumed from collateral, not the pool). */
  totalRecovered: number;
  /** Σ shortfall across netted SI groups — ranks unsecured. */
  totalShortfall: number;
  /**
   * Full owed for secured creditors whose SI has **no** linked asset, plus any
   * creditor flagged `secured` with no SI link at all. Deducted in full as a
   * secured-priority line (today's behaviour — nothing to net against).
   */
  totalUnlinkedSecured: number;
  /** Encumbered assetId → secured balance apportioned to it (this scenario). */
  securedByAsset: Map<string, number>;
  /** Encumbered assetId → residual (net-of-security) value (this scenario). */
  residualByAsset: Map<string, number>;
  /** Encumbered assetId → creditor-traceable breakdown of its deduction. */
  breakdownByAsset: Map<string, AssetSecuredBreakdown>;
}

/**
 * Net a single gross figure against a fixed secured deduction — the one rule
 * shared by the Asset table columns and the detail-panel "Net" popover so the
 * two never disagree. `null` gross passes through (a blank column stays blank);
 * the net is floored at $0 (security never makes an asset negative).
 */
export function netOfSecurity(
  gross: number | null,
  deduction: number,
): number | null {
  if (gross == null) return null;
  return Math.max(0, gross - deduction);
}

// ---------------------------------------------------------------------------
// Core computation
// ---------------------------------------------------------------------------

/**
 * Compute secured netting for one asset-value scenario.
 *
 * @param assets             all asset rows (merged/excluded are ignored)
 * @param liabilities        all creditor rows (merged are ignored)
 * @param securityInterests  all SI rows (for their own owedAmount figure)
 * @param links              active recordLinks for the job (listLinksForJob is
 *                           already active-only, but we tolerate a `status` field)
 * @param selector           per-asset value for the scenario being computed
 */
export function computeSecuredNetting(
  assets: AssetEntryUI[],
  liabilities: NettingLiability[],
  securityInterests: NettingSecurityInterest[],
  links: NettingLink[],
  selector: AssetValueSelector,
): SecuredNettingResult {
  // ----- Index assets (active, non-excluded) → scenario value ----------------
  const assetValue = new Map<string, number>();
  for (const a of assets) {
    if (a.status !== "active" || a.isExcluded) continue;
    assetValue.set(a._id, selector(a) ?? 0);
  }
  const totalGrossAssets = Array.from(assetValue.values()).reduce(
    (sum, v) => sum + v,
    0,
  );

  // ----- Index creditor amounts (active, non-excluded) -----------------------
  const creditorAmount = new Map<string, number>();
  for (const l of liabilities) {
    if (l.status !== "active" || l.isExcluded) continue;
    creditorAmount.set(l._id, l.amount ?? 0);
  }

  // ----- Index each SI's own owed figure (active) ----------------------------
  // This is the canonical debt the practitioner sees on the SI panel — a PoD /
  // document balance, a manual override, or the linked-creditor rollup. When
  // present we net against THIS figure so "what you see is what's netted",
  // rather than re-summing whatever creditors happen to be linked.
  const siOwnOwed = new Map<string, number>();
  // Excluded SIs are dropped from netting entirely — their links are skipped
  // below so they neither deduct nor reduce their collateral.
  const excludedSIs = new Set<string>();
  for (const si of securityInterests) {
    if (si.status !== "active") continue;
    if (si.isExcluded) {
      excludedSIs.add(si._id);
      continue;
    }
    if (si.owedAmount != null) siOwnOwed.set(si._id, si.owedAmount);
  }

  // ----- Walk SI links → group assets + creditors per security interest -------
  const assetIdsBySI = new Map<string, string[]>();
  const creditorIdsBySI = new Map<string, string[]>();
  // How many SIs each asset is linked to (for apportioning shared collateral).
  const siCountByAsset = new Map<string, number>();
  // Creditor ids accounted for via an SI link (so we don't double-count below).
  const linkedSecuredCreditors = new Set<string>();

  for (const link of links) {
    if (link.detailType !== "security_interest") continue;
    if (excludedSIs.has(link.detailId)) continue;
    if (link.anchorType === "asset") {
      if (!assetValue.has(link.anchorId)) continue; // inactive/excluded asset
      const arr = assetIdsBySI.get(link.detailId) ?? [];
      arr.push(link.anchorId);
      assetIdsBySI.set(link.detailId, arr);
      siCountByAsset.set(
        link.anchorId,
        (siCountByAsset.get(link.anchorId) ?? 0) + 1,
      );
    } else if (link.anchorType === "creditor") {
      if (!creditorAmount.has(link.anchorId)) continue; // inactive creditor
      const arr = creditorIdsBySI.get(link.detailId) ?? [];
      arr.push(link.anchorId);
      creditorIdsBySI.set(link.detailId, arr);
      linkedSecuredCreditors.add(link.anchorId);
    }
  }

  // ----- Build SI groups, splitting netted vs unlinked-secured ----------------
  const groups: SecuredInterestGroup[] = [];
  let totalRecovered = 0;
  let totalShortfall = 0;
  let totalUnlinkedSecured = 0;
  const securedByAsset = new Map<string, number>();
  const residualByAsset = new Map<string, number>();
  const breakdownByAsset = new Map<string, AssetSecuredBreakdown>();
  let residualToPoolTotal = 0;
  // Track which assets are encumbered so we can sum the unencumbered remainder.
  const encumberedAssets = new Set<string>();

  const allSiIds = new Set<string>([
    ...assetIdsBySI.keys(),
    ...creditorIdsBySI.keys(),
  ]);

  for (const siId of allSiIds) {
    const assetIds = assetIdsBySI.get(siId) ?? [];
    const creditorIds = creditorIdsBySI.get(siId) ?? [];
    // Effective owed: the SI's own figure when it has one ("what you see is
    // what's netted"); otherwise fall back to the sum of linked creditors.
    const creditorSum = creditorIds.reduce(
      (sum, cid) => sum + (creditorAmount.get(cid) ?? 0),
      0,
    );
    const declaredOwed = siOwnOwed.get(siId);
    const owed = declaredOwed != null ? declaredOwed : creditorSum;

    if (assetIds.length === 0) {
      // No collateral to net against → full secured-priority deduction (fallback).
      totalUnlinkedSecured += owed;
      continue;
    }

    // Apportioned collateral: an asset shared across N SIs contributes value/N.
    let collateralValue = 0;
    for (const aid of assetIds) {
      encumberedAssets.add(aid);
      const share = (assetValue.get(aid) ?? 0) / (siCountByAsset.get(aid) || 1);
      collateralValue += share;
    }

    const recovered = Math.min(owed, collateralValue);
    const residualToPool = Math.max(0, collateralValue - recovered);
    const shortfall = Math.max(0, owed - recovered);

    groups.push({
      siId,
      assetIds,
      collateralValue,
      owed,
      recovered,
      residualToPool,
      shortfall,
    });
    totalRecovered += recovered;
    totalShortfall += shortfall;
    residualToPoolTotal += residualToPool;

    // Apportion the owed balance + residual back to each linked asset (pro-rata
    // by its apportioned value) so the Asset list can show one figure per asset.
    const declaredOverride = declaredOwed != null;
    for (const aid of assetIds) {
      const share = (assetValue.get(aid) ?? 0) / (siCountByAsset.get(aid) || 1);
      const apportionedOwed =
        collateralValue > 0 ? owed * (share / collateralValue) : 0;
      const value = assetValue.get(aid) ?? 0;
      securedByAsset.set(aid, (securedByAsset.get(aid) ?? 0) + apportionedOwed);
      const prevResidual = residualByAsset.get(aid) ?? value;
      residualByAsset.set(aid, Math.max(0, prevResidual - apportionedOwed));

      // Record the creditor-traceable breakdown for this asset/SI. Creditor
      // amounts are apportioned to the SI's deduction against THIS asset
      // (`apportionedOwed / creditorSum`) so they always sum EXACTLY to
      // `siDeduction` — even when the SI's owed figure (a declared override /
      // PoD balance) differs from the linked-creditor sum, or when collateral
      // is shared across assets. This keeps the panel's calc self-consistent:
      // Σ creditors = SI subtotal, Σ SI subtotals = total deduction = base − net.
      const factor = creditorSum > 0 ? apportionedOwed / creditorSum : 0;
      const bd = breakdownByAsset.get(aid) ?? {
        totalDeduction: 0,
        sis: [],
      };
      bd.totalDeduction += apportionedOwed;
      bd.sis.push({
        siId,
        siDeduction: apportionedOwed,
        declaredOverride,
        creditors: creditorIds.map((cid) => ({
          creditorId: cid,
          amount: (creditorAmount.get(cid) ?? 0) * factor,
        })),
      });
      breakdownByAsset.set(aid, bd);
    }
  }

  // ----- Creditors flagged secured but with NO SI link at all -----------------
  // Preserve today's behaviour: deduct them in full as secured priority.
  for (const l of liabilities) {
    if (l.status !== "active" || l.isExcluded) continue;
    if (l.secured === true && !linkedSecuredCreditors.has(l._id)) {
      totalUnlinkedSecured += l.amount ?? 0;
    }
  }

  // ----- Net realisable assets = unencumbered + residual-to-pool --------------
  const unencumbered = Array.from(assetValue).reduce(
    (sum, [aid, v]) => (encumberedAssets.has(aid) ? sum : sum + v),
    0,
  );
  const totalNetAssets = unencumbered + residualToPoolTotal;

  return {
    groups,
    totalGrossAssets,
    totalNetAssets,
    totalRecovered,
    totalShortfall,
    totalUnlinkedSecured,
    securedByAsset,
    residualByAsset,
    breakdownByAsset,
  };
}
